import { ChevronDown, House, Minus, Plus } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { controller } from "../app/controller.ts";
import { useApp } from "../state/store.ts";
import { usePopover } from "./usePopover.ts";

const n = (v: number) => v.toLocaleString("en-US").replace(/,/g, " ");

/**
 * Everything along the bottom of the map: where the mouse points (click it to go somewhere) on the left,
 * loading/performance, zoom, home and the compass on the right. Pointer events only on the pills.
 */
export function StatusBar() {
  const mapColoursOnly = useApp((s) => s.mapColoursOnly);
  const demo = useApp((s) => s.demo);
  return (
    <footer className="statusbar">
      <Coords />
      {demo && <span className="map-pill note" title="A world MC Planner makes up itself, in 3D blocks: its own built-in blocks, or your game's textures when they're loaded. No Minecraft files needed. Your plan is kept in this browser. Close it from the world menu (top left) to open your own world.">Demo island · stays on this computer</span>}
      {mapColoursOnly && !demo && <span className="map-pill note" title="You joined without block textures: the map shows the host's colours, no 3D blocks. Leave the world and open the invite link again to get the textures from Mojang (or load your own jar).">Map colours only</span>}
      <span className="spacer" />
      <div className="nav-cluster">
        <Stats />
        <span className="nav-group glass">
          <button className="btn btn-ghost btn-icon" onClick={() => controller.engine?.zoomBy(1.6)} title="Zoom out · −" aria-label="Zoom out"><Minus size={15} aria-hidden="true" /></button>
          <button className="btn btn-ghost btn-icon" onClick={() => controller.engine?.zoomBy(1 / 1.6)} title="Zoom in · +" aria-label="Zoom in"><Plus size={15} aria-hidden="true" /></button>
          <span className="nav-sep" aria-hidden="true" />
          <HomeButton />
        </span>
        <Compass />
      </div>
    </footer>
  );
}

/** x y z under the mouse; a click turns it into "Go to x z". */
function Coords() {
  const hover = useApp((s) => s.hover);
  const [editing, setEditing] = useState(false);
  const [goto, setGoto] = useState("");
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => { if (editing) input.current?.focus(); }, [editing]);
  if (editing) {
    return (
      <form className="map-pill coords-pill goto" onSubmit={(e) => {
        e.preventDefault();
        const nums = goto.match(/-?\d+/g)?.map(Number) ?? [];
        if (nums.length >= 2) {
          const [x, z] = nums.length >= 3 ? [nums[0], nums[2]] : [nums[0], nums[1]];
          controller.goTo(x, z);
          setEditing(false);
          setGoto("");
        }
      }}>
        <span className="k">Go to</span>
        <input ref={input} placeholder="x z" value={goto} onChange={(e) => setGoto(e.target.value)} onBlur={() => { if (!goto.trim()) setEditing(false); }} onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); setEditing(false); } }} aria-label="Go to coordinates: x and z" />
        <kbd>Enter</kbd>
      </form>
    );
  }
  return (
    <button className="map-pill coords-pill" onClick={() => setEditing(true)} title="Go to coordinates" aria-label={hover ? `x ${hover.x}, y ${hover.y}, z ${hover.z}. Go to coordinates` : "Go to coordinates"}>
      {hover ? (
        <>
          <span><span className="k">x</span> {n(hover.x)}</span>
          <span><span className="k">y</span> {hover.y}</span>
          <span><span className="k">z</span> {n(hover.z)}</span>
          <span className="k">chunk {hover.x >> 4} · {hover.z >> 4}</span>
        </>
      ) : <span className="k">Go to x z</span>}
    </button>
  );
}

function Stats() {
  const stats = useApp((s) => s.stats);
  if (!stats) return null;
  const loading = stats.regionsLoaded < stats.regionsTotal || stats.pending > 0;
  return (
    <span className="map-pill stats" title={`${n(stats.chunks)} chunks in 3D · ${(stats.triangles / 1e6).toFixed(1)} M triangles · ${stats.fps} fps`}>
      <i className={`stat-dot${loading ? " busy" : ""}`} aria-hidden="true" />
      {stats.regionsLoaded < stats.regionsTotal ? `map ${stats.regionsLoaded}/${stats.regionsTotal} regions · ` : ""}
      {n(stats.chunks)} chunks{stats.pending ? ` (+${stats.pending})` : ""} · {stats.fps} fps
    </span>
  );
}

function HomeButton() {
  const home = useApp((s) => s.home);
  const hover = useApp((s) => s.hover);
  const m = usePopover<HTMLSpanElement>();
  return (
    <span className="home-group" ref={m.ref}>
      <button className="btn btn-ghost home-btn" onClick={() => controller.goHome()} title={home ? `Fly home · H · x ${home.x} y ${home.y} z ${home.z}${home.setBy ? ` · set by ${home.setBy}` : ""}` : "No home set yet: goes to spawn · H"} aria-label="Fly home (H)">
        <House size={15} aria-hidden="true" />Home<kbd>H</kbd>
      </button>
      <button className="btn btn-ghost btn-icon home-more" aria-expanded={m.open} onClick={m.toggle} title="Set home" aria-label="Set home"><ChevronDown size={14} aria-hidden="true" /></button>
      {m.open && (
        <div className="menu home-menu" role="menu">
          <button role="menuitem" className="menu-item" onClick={() => { controller.setHome("view"); m.close(); }}>
            <House size={15} aria-hidden="true" />
            <span className="grow">Set home to the middle of the view<span className="menu-sub">…or point at a block and press <kbd>Shift</kbd> <kbd>H</kbd>{hover ? ` (now ${hover.x} ${hover.z})` : ""}</span></span>
          </button>
          {home && (
            <button role="menuitem" className="menu-item" onClick={() => { controller.goHome(); m.close(); }}>
              <span className="menu-icon-gap" aria-hidden="true" />
              <span className="grow">Fly home<span className="menu-sub mono">x {home.x} · y {home.y} · z {home.z}</span></span>
            </button>
          )}
          {home && <button role="menuitem" className="menu-item danger" onClick={() => { controller.clearHome(); m.close(); }}><span className="menu-icon-gap" aria-hidden="true" /><span className="grow">Remove home</span></button>}
          <div className="menu-sep" />
          <p className="menu-note">Home is shared with everyone in this world and is where the world opens.</p>
        </div>
      )}
    </span>
  );
}

/** Points north on screen; a click turns the view to face north. */
function Compass() {
  const needle = useRef<SVGSVGElement>(null);
  useEffect(() => {
    let raf = 0, last = NaN;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      const h = controller.engine?.heading();
      if (h === undefined || !needle.current || Math.abs(h - last) < 0.2) return;
      last = h;
      needle.current.style.transform = `rotate(${-h}deg)`;
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, []);
  return (
    <button className="compass glass" onClick={() => controller.engine?.faceNorth()} title="Face north" aria-label="Compass: turn the view to face north">
      <svg ref={needle} width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
        <path d="M10 2.5 12.6 10H7.4Z" fill="#f06484" />
        <path d="M10 17.5 7.4 10h5.2Z" fill="#93a1a9" />
        <circle cx="10" cy="10" r="1.3" fill="#15191c" />
      </svg>
    </button>
  );
}
